/**
 * ListView component for calendar
 * Displays events grouped by month and day with headers
 */

'use client';

import type { Event } from '../types';
import { useEventsComponents } from '../config';
import { Calendar, Clock, MapPin } from 'lucide-react';
import { useMemo } from 'react';
import { darkenHexColorWithOpacity as defaultDarkenHexColor } from '../utils/color';

interface ListViewProps {
  events: Event[];
  baseUrl?: string;
}

interface GroupedEvents {
  monthYear: string;
  monthDate: Date;
  days: {
    dayLabel: string;
    dayDate: Date;
    events: Event[];
  }[];
}

/**
 * Extract event type color from event
 */
function getEventTypeColor(event: Event): string | undefined {
  const eventTypeItem = Array.isArray(event.eventType) ? event.eventType[0] : event.eventType;
  return eventTypeItem && 'eventTypeColor' in eventTypeItem
    ? (eventTypeItem.eventTypeColor ?? undefined)
    : undefined;
}

export function ListView({ events, baseUrl = '/events' }: ListViewProps) {
  const config = useEventsComponents();
  const Link = config.LinkComponent;
  const darkenHexColorWithOpacity = config.darkenHexColorWithOpacity || defaultDarkenHexColor;

  // Group events by month and day
  const groupedEvents = useMemo(() => {
    const groups: GroupedEvents[] = [];
    const monthMap = new Map<string, GroupedEvents>();

    // Sort events by start date
    const sortedEvents = [...events].sort((a, b) => {
      const aStart = a.eventDateTimeRange?.start?.timestamp || 0;
      const bStart = b.eventDateTimeRange?.start?.timestamp || 0;
      return aStart - bStart;
    });

    sortedEvents.forEach(event => {
      const startTimestamp = event.eventDateTimeRange?.start?.timestamp;
      if (!startTimestamp) return;

      const eventDate = new Date(startTimestamp * 1000);
      const monthYearKey = `${eventDate.getFullYear()}-${eventDate.getMonth()}`;
      const dayKey = eventDate.toDateString();

      // Get or create month group
      let monthGroup = monthMap.get(monthYearKey);
      if (!monthGroup) {
        monthGroup = {
          monthYear: eventDate.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' }),
          monthDate: new Date(eventDate.getFullYear(), eventDate.getMonth(), 1),
          days: [],
        };
        monthMap.set(monthYearKey, monthGroup);
        groups.push(monthGroup);
      }

      // Get or create day group
      let dayGroup = monthGroup.days.find(d => d.dayDate.toDateString() === dayKey);
      if (!dayGroup) {
        dayGroup = {
          dayLabel: eventDate.toLocaleDateString('en-GB', {
            weekday: 'long',
            day: 'numeric',
            month: 'long',
          }),
          dayDate: new Date(eventDate),
          events: [],
        };
        monthGroup.days.push(dayGroup);
      }

      dayGroup.events.push(event);
    });

    return groups;
  }, [events]);

  if (groupedEvents.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
        <Calendar className="mb-4 h-12 w-12 opacity-50" />
        <p className="text-lg">No events found</p>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {groupedEvents.map(monthGroup => (
        <div key={monthGroup.monthYear}>
          {/* Month Header */}
          <h2 className="mb-4 text-2xl font-bold text-primary">{monthGroup.monthYear}</h2>

          {/* Days in month */}
          <div className="space-y-6">
            {monthGroup.days.map(dayGroup => (
              <div key={dayGroup.dayDate.toISOString()} className="space-y-3">
                {/* Day Header */}
                <h3 className="flex items-center gap-2 text-lg font-semibold text-foreground">
                  <Calendar className="h-5 w-5 text-primary" />
                  {dayGroup.dayLabel}
                </h3>

                {/* Events for this day */}
                <div className="ml-7 space-y-2">
                  {dayGroup.events.map(event => {
                    const startTimestamp = event.eventDateTimeRange?.start?.timestamp;
                    const startDate = startTimestamp ? new Date(startTimestamp * 1000) : null;
                    const startTime = startDate
                      ? startDate.toLocaleTimeString('en-GB', {
                          hour: '2-digit',
                          minute: '2-digit',
                        })
                      : '';

                    const eventLink = event.path;
                    if (!eventLink) return null;

                    const eventTypeColor = getEventTypeColor(event);

                    const className = `group flex items-start gap-4 rounded-lg border p-4 transition-all hover:shadow-md hover:border-primary cursor-pointer ${
                      eventTypeColor ? '' : 'bg-background'
                    }`;
                    const style = {
                      backgroundColor: eventTypeColor
                        ? darkenHexColorWithOpacity(eventTypeColor, 90, 0.05)
                        : undefined,
                      borderLeftColor: eventTypeColor || undefined,
                      borderLeftWidth: eventTypeColor ? '4px' : undefined,
                    };

                    const content = (
                      <>
                        {/* Time */}
                        <div className="flex-shrink-0 w-20 text-center">
                          <div className="flex flex-col items-center gap-1">
                            <Clock className="h-4 w-4 text-muted-foreground" />
                            <span className="text-sm font-semibold">{startTime}</span>
                          </div>
                        </div>

                        {/* Event Details */}
                        <div className="flex-1 min-w-0">
                          <h4 className="mb-1 text-base font-semibold group-hover:text-primary">
                            {event.title}
                          </h4>
                          {event.eventLocation && (
                            <div className="flex items-center gap-1 text-sm text-muted-foreground">
                              <MapPin className="h-3 w-3 flex-shrink-0" />
                              <span className="truncate">{event.eventLocation}</span>
                            </div>
                          )}
                        </div>
                      </>
                    );

                    // Use Link for navigation
                    return (
                      <Link key={event.id} href={eventLink || ''} className={className} style={style}>
                        {content}
                      </Link>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

